import {
  type LandingTestimonialAdmin,
  type LandingTestimonialFields,
  MAX_LANDING_TESTIMONIALS,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  useInvalidateLandingTestimonials,
  useLandingTestimonialsQuery,
} from '@/entities/landing/testimonial';
import {
  LandingTestimonialCard,
  LandingTestimonialFormModal,
  useAddLandingTestimonial,
  useDeleteLandingTestimonial,
  useMoveLandingTestimonial,
  useReorderLandingTestimonials,
  useReplaceLandingTestimonialImage,
  useUpdateLandingTestimonial,
} from '@/features/landing/testimonials';
import { movedOrder, useReorderDrag } from '@/shared/lib';
import { Button, ContentCard, EmptyState } from '@/shared/ui';

type TestimonialForm =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly item: LandingTestimonialAdmin };

/**
 * HOME 기업 고객 사례 관리 — 등록·글 수정·사진 교체·순서·삭제. 위에서부터 HOME 에 차례로 나온다.
 * 기업과 함께한 제작 사례를 사진·제목·내용·담당자로 소개하는 카드다. 하나도 없으면 HOME 에 섹션이 나오지 않는다.
 */
export const LandingTestimonialManager = (): ReactElement => {
  const itemsQuery = useLandingTestimonialsQuery();
  const invalidate = useInvalidateLandingTestimonials();
  const addAction = useAddLandingTestimonial({ onSuccess: invalidate });
  const updateAction = useUpdateLandingTestimonial({ onSuccess: invalidate });
  const replaceAction = useReplaceLandingTestimonialImage({ onSuccess: invalidate });
  const moveAction = useMoveLandingTestimonial({ onSuccess: invalidate });
  const deleteAction = useDeleteLandingTestimonial({ onSuccess: invalidate });
  const reorderAction = useReorderLandingTestimonials({ onSuccess: invalidate });
  const [form, setForm] = useState<TestimonialForm | null>(null);

  const items = itemsQuery.data ?? [];
  const ids = items.map((item) => item.id);
  const isFull = items.length >= MAX_LANDING_TESTIMONIALS;
  // 한 항목이라도 바뀌는 중이면 다른 카드의 버튼도 잠근다 — 히어로 구역과 같은 규칙.
  const isBusy =
    addAction.isPending ||
    updateAction.isPending ||
    moveAction.isPending ||
    deleteAction.isPending ||
    reorderAction.isPending ||
    replaceAction.pendingId !== null;
  const listError =
    replaceAction.error ??
    moveAction.error?.message ??
    reorderAction.error?.message ??
    deleteAction.error?.message ??
    null;
  // 손잡이를 끌어 다른 카드 앞·뒤에 놓으면 전체 순서를 한 번에 제출한다.
  const drag = useReorderDrag({
    scope: 'landing-testimonials',
    ids,
    disabled: isBusy,
    axis: 'x',
    onMove: (from, to) => {
      clearListErrors();
      reorderAction.reorderItems(movedOrder(ids, from, to));
    },
  });

  // 모달을 열고 닫을 때 지난 업로드·서버 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeForm = (next: TestimonialForm | null): void => {
    addAction.reset();
    updateAction.reset();
    setForm(next);
  };

  const handleSubmit = async (
    fields: LandingTestimonialFields,
    file: File | null,
  ): Promise<void> => {
    if (form === null) {
      return;
    }

    const isDone =
      form.mode === 'create'
        ? file !== null && (await addAction.addItem(file, fields))
        : await updateAction.updateItem(form.item.id, fields);

    if (isDone) {
      changeForm(null);
    }
  };

  // 사진 교체·이동·삭제는 오류 한 줄(listError)을 함께 쓴다. 새 요청을 보내기 전에 지난 오류를 모두 지운다.
  function clearListErrors(): void {
    replaceAction.reset();
    moveAction.reset();
    reorderAction.reset();
    deleteAction.reset();
  }

  const handleReplace = (id: string, file: File): void => {
    clearListErrors();
    void replaceAction.replaceImage(id, file);
  };

  const handleMove = (id: string, direction: 'UP' | 'DOWN'): void => {
    clearListErrors();
    moveAction.moveItem(id, direction);
  };

  const handleDelete = (item: LandingTestimonialAdmin, order: number): void => {
    if (window.confirm(`기업 고객 사례 ${order}번 "${item.title}"을 삭제하시겠습니까?`)) {
      clearListErrors();
      deleteAction.deleteItem(item.id);
    }
  };

  return (
    <ContentCard
      title="기업 고객 사례"
      description={`기업과 함께한 제작 사례 카드(사진·제목·내용·담당자). HOME 포트폴리오 아래에 위에서부터 차례로 나옵니다(한 줄 4장). 최대 개.`}
      actions={
        <>
          <span className="text-sm text-muted-foreground tabular-nums">
            {items.length}/{MAX_LANDING_TESTIMONIALS}
          </span>
          <Button
            size="sm"
            disabled={isFull || isBusy}
            onClick={() => changeForm({ mode: 'create' })}
          >
            기업 고객 사례 추가
          </Button>
        </>
      }
    >
      {listError !== null ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {listError}
        </p>
      ) : null}

      {itemsQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : itemsQuery.isError ? (
        <EmptyState isError>
          기업 고객 사례를 불러오지 못했습니다.{' '}
          <button type="button" className="underline" onClick={() => void itemsQuery.refetch()}>
            다시 시도
          </button>
        </EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>
          등록된 기업 고객 사례가 없습니다. 추가하면 HOME 포트폴리오 아래 "기업 고객 사례" 구역에
          나옵니다.
        </EmptyState>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {items.map((item, index) => (
            <LandingTestimonialCard
              key={item.id}
              item={item}
              order={index + 1}
              isFirst={index === 0}
              isLast={index === items.length - 1}
              isBusy={isBusy}
              onMove={(direction) => handleMove(item.id, direction)}
              onEdit={() => changeForm({ mode: 'edit', item })}
              onReplace={(file) => handleReplace(item.id, file)}
              onDelete={() => handleDelete(item, index + 1)}
              handleProps={drag.handleProps(item.id)}
              rowProps={drag.rowProps(item.id)}
              dropEdge={
                drag.target?.id === item.id ? (drag.target.after ? 'after' : 'before') : undefined
              }
            />
          ))}
        </ul>
      )}

      {form !== null ? (
        <LandingTestimonialFormModal
          mode={form.mode}
          initialFields={
            form.mode === 'edit'
              ? {
                  title: form.item.title,
                  body: form.item.body,
                  author: form.item.author,
                }
              : null
          }
          isPending={form.mode === 'create' ? addAction.isPending : updateAction.isPending}
          errorMessage={form.mode === 'create' ? addAction.error : updateAction.error}
          onSubmit={(fields, file) => void handleSubmit(fields, file)}
          onClose={() => changeForm(null)}
        />
      ) : null}
    </ContentCard>
  );
};
